/**
 * STORYBOARD UI - Panel Selector Module
 * Allows users to draw a selection on a panel image and extract it as a new panel
 * Ported from /features/grid-splitter/script.js
 */

import {
  getActiveIdeaId,
  addPanelsToPool,
  saveToDatabase,
  isDatabaseIdea
} from '../state.js';

// ============================================================================
// MODULE STATE
// ============================================================================

const selectorState = {
  isOpen: false,
  sourcePanel: null,        // { url, index, ideaId }
  image: null,              // Loaded HTMLImageElement
  canvas: null,             // Canvas element ref
  ctx: null,                // 2D context
  isSelecting: false,       // Currently drawing new selection
  isDragging: false,        // Dragging to move selection
  isResizing: false,        // Resizing via handles
  resizeHandle: null,       // Which handle: 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  dragStart: null,          // { x, y, selX, selY } for move operations
  selectionStart: null,     // { x, y } canvas coords where mouse went down
  selection: null,          // { x, y, width, height, canvasX, canvasY, canvasWidth, canvasHeight }
  scale: { x: 1, y: 1 }     // Canvas-to-natural ratio
};

// Handle size for resize corners/edges
const HANDLE_SIZE = 12;
const HANDLE_HIT_SIZE = 16; // Larger hit area for easier grabbing

// ============================================================================
// MODAL CREATION & LIFECYCLE
// ============================================================================

/**
 * Open the panel selector modal
 * @param {string} panelUrl - URL of the panel image
 * @param {number} panelIndex - Index of the source panel
 * @param {string} ideaId - The idea ID
 */
export function openPanelSelector(panelUrl, panelIndex, ideaId) {
  if (selectorState.isOpen) {
    closePanelSelector();
  }

  selectorState.sourcePanel = { url: panelUrl, index: panelIndex, ideaId };
  selectorState.isOpen = true;
  selectorState.selection = null;
  selectorState.selectionStart = null;

  // Create modal HTML
  const modalHtml = `
    <div class="panel-selector-modal" id="panelSelectorModal">
      <div class="panel-selector-container">
        <div class="panel-selector-header">
          <button class="panel-selector-close" data-action="close">&times;</button>
          <h3>SELECT REGION</h3>
          <div class="panel-selector-header-spacer"></div>
        </div>

        <div class="panel-selector-body">
          <div class="panel-selector-canvas-wrapper" id="panelSelectorCanvasWrapper">
            <canvas id="panelSelectorCanvas"></canvas>
            <div class="panel-selector-info" id="panelSelectorInfo">
              <span class="selection-dimensions"></span>
            </div>
            <div class="panel-selector-loading" id="panelSelectorLoading">
              <div class="loading-spinner"></div>
              <span>Loading image...</span>
            </div>
          </div>
          <div class="panel-selector-hint-row">
            <p class="panel-selector-hint">Draw selection, drag to move, resize with handles</p>
            <button class="btn-select-all" data-action="selectAll">SELECT ALL</button>
          </div>
        </div>

        <div class="panel-selector-footer">
          <button class="btn-secondary" data-action="cancel">CANCEL</button>
          <button class="btn-primary" data-action="extract" id="btnExtractSelection" disabled>
            EXTRACT SELECTION
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  const modal = document.getElementById('panelSelectorModal');

  // Event handlers
  modal.addEventListener('click', handleModalClick);

  // Keyboard handler
  document.addEventListener('keydown', handleKeyDown);

  // Load the image
  loadImageToCanvas(panelUrl);

  console.log('[PanelSelector] Opened for panel', panelIndex + 1);
}

/**
 * Close the panel selector modal
 */
export function closePanelSelector() {
  const modal = document.getElementById('panelSelectorModal');
  if (modal) {
    modal.remove();
  }

  // Clean up state
  selectorState.isOpen = false;
  selectorState.sourcePanel = null;
  selectorState.image = null;
  selectorState.canvas = null;
  selectorState.ctx = null;
  selectorState.isSelecting = false;
  selectorState.isDragging = false;
  selectorState.isResizing = false;
  selectorState.resizeHandle = null;
  selectorState.dragStart = null;
  selectorState.selection = null;
  selectorState.selectionStart = null;

  // Remove keyboard handler
  document.removeEventListener('keydown', handleKeyDown);

  console.log('[PanelSelector] Closed');
}

// ============================================================================
// EVENT HANDLERS
// ============================================================================

/**
 * Handle clicks in the modal
 */
function handleModalClick(e) {
  const action = e.target.closest('[data-action]')?.dataset.action;

  switch (action) {
    case 'close':
    case 'cancel':
      closePanelSelector();
      break;
    case 'extract':
      extractSelection();
      break;
    case 'selectAll':
      selectAll();
      break;
    default:
      // Click outside modal content closes it
      if (e.target.classList.contains('panel-selector-modal')) {
        closePanelSelector();
      }
  }
}

/**
 * Handle keyboard events
 */
function handleKeyDown(e) {
  if (!selectorState.isOpen) return;

  if (e.key === 'Escape') {
    closePanelSelector();
  }

  // Ctrl/Cmd + A = select all
  if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
    e.preventDefault();
    selectAll();
  }
}

// ============================================================================
// IMAGE LOADING & CANVAS SETUP
// ============================================================================

/**
 * Load image and set up the selection overlay canvas
 * Uses img element for display (no CORS needed) and canvas overlay for selection
 */
function loadImageToCanvas(imageUrl) {
  const loadingEl = document.getElementById('panelSelectorLoading');
  const wrapper = document.getElementById('panelSelectorCanvasWrapper');

  if (!wrapper) return;

  // Create image element (no CORS issues for display)
  const img = new Image();

  img.onload = () => {
    selectorState.image = img;

    // Calculate display size to fit in viewport while maintaining aspect ratio
    const maxWidth = Math.min(window.innerWidth * 0.85, 1200);
    const maxHeight = window.innerHeight * 0.6;

    let displayWidth = img.naturalWidth;
    let displayHeight = img.naturalHeight;

    // Scale down if needed
    if (displayWidth > maxWidth) {
      const ratio = maxWidth / displayWidth;
      displayWidth = maxWidth;
      displayHeight = displayHeight * ratio;
    }

    if (displayHeight > maxHeight) {
      const ratio = maxHeight / displayHeight;
      displayHeight = displayHeight * ratio;
      displayWidth = displayWidth * ratio;
    }

    // Store scale for coordinate conversion
    selectorState.scale = {
      x: img.naturalWidth / displayWidth,
      y: img.naturalHeight / displayHeight
    };

    // Build wrapper with image and canvas overlay
    wrapper.innerHTML = `
      <img src="${imageUrl}" alt="Source storyboard"
           style="display: block; width: ${displayWidth}px; height: ${displayHeight}px;"
           id="panelSelectorImage" />
      <canvas id="panelSelectorCanvas"
              width="${displayWidth}" height="${displayHeight}"
              style="position: absolute; top: 0; left: 0; cursor: crosshair;"></canvas>
    `;

    // Get canvas reference
    const canvas = document.getElementById('panelSelectorCanvas');
    selectorState.canvas = canvas;
    selectorState.ctx = canvas.getContext('2d');

    // Hide loading
    if (loadingEl) loadingEl.style.display = 'none';

    // Attach selection handlers to canvas
    canvas.addEventListener('mousedown', handleSelectionStart);
    canvas.addEventListener('mousemove', handleSelectionMove);
    canvas.addEventListener('mouseup', handleSelectionEnd);
    canvas.addEventListener('mouseleave', handleSelectionEnd);

    // Touch support
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    console.log('[PanelSelector] Image loaded:', img.naturalWidth, 'x', img.naturalHeight, '-> display:', displayWidth, 'x', displayHeight);
  };

  img.onerror = () => {
    console.error('[PanelSelector] Failed to load image:', imageUrl);
    if (loadingEl) {
      loadingEl.innerHTML = '<span style="color: #f00;">Failed to load image</span>';
    }
  };

  img.src = imageUrl;
}

// ============================================================================
// SELECTION DRAWING & INTERACTION
// ============================================================================

/**
 * Get canvas coordinates from mouse event, clamped to canvas bounds
 */
function getCanvasCoords(e, clamp = true) {
  const canvas = selectorState.canvas;
  if (!canvas) return { x: 0, y: 0 };

  const rect = canvas.getBoundingClientRect();
  let x = e.clientX - rect.left;
  let y = e.clientY - rect.top;

  if (clamp) {
    x = Math.max(0, Math.min(x, canvas.width));
    y = Math.max(0, Math.min(y, canvas.height));
  }

  return { x, y };
}

/**
 * Check which resize handle (if any) is at the given coordinates
 * Returns: 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w', or null
 */
function getHandleAtPoint(x, y) {
  const sel = selectorState.selection;
  if (!sel) return null;

  const { canvasX, canvasY, canvasWidth, canvasHeight } = sel;
  const hs = HANDLE_HIT_SIZE;

  // Define all handle positions
  const handles = {
    nw: { x: canvasX, y: canvasY },
    ne: { x: canvasX + canvasWidth, y: canvasY },
    sw: { x: canvasX, y: canvasY + canvasHeight },
    se: { x: canvasX + canvasWidth, y: canvasY + canvasHeight },
    n:  { x: canvasX + canvasWidth/2, y: canvasY },
    s:  { x: canvasX + canvasWidth/2, y: canvasY + canvasHeight },
    w:  { x: canvasX, y: canvasY + canvasHeight/2 },
    e:  { x: canvasX + canvasWidth, y: canvasY + canvasHeight/2 }
  };

  // Check each handle (corners first for priority)
  const handleOrder = ['nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e'];
  for (const handle of handleOrder) {
    const h = handles[handle];
    if (x >= h.x - hs/2 && x <= h.x + hs/2 &&
        y >= h.y - hs/2 && y <= h.y + hs/2) {
      return handle;
    }
  }

  return null;
}

/**
 * Check if point is inside the selection rectangle
 */
function isPointInSelection(x, y) {
  const sel = selectorState.selection;
  if (!sel) return false;

  const { canvasX, canvasY, canvasWidth, canvasHeight } = sel;
  return x >= canvasX && x <= canvasX + canvasWidth &&
         y >= canvasY && y <= canvasY + canvasHeight;
}

/**
 * Update cursor based on what's under the mouse
 */
function updateCursor(x, y) {
  const canvas = selectorState.canvas;
  if (!canvas) return;

  const handle = getHandleAtPoint(x, y);
  if (handle) {
    const cursors = {
      'nw': 'nwse-resize', 'se': 'nwse-resize',
      'ne': 'nesw-resize', 'sw': 'nesw-resize',
      'n': 'ns-resize', 's': 'ns-resize',
      'e': 'ew-resize', 'w': 'ew-resize'
    };
    canvas.style.cursor = cursors[handle];
  } else if (isPointInSelection(x, y)) {
    canvas.style.cursor = 'move';
  } else {
    canvas.style.cursor = 'crosshair';
  }
}

/**
 * Handle mouse down - start selection, drag, or resize
 */
function handleSelectionStart(e) {
  if (!selectorState.image) return;

  const coords = getCanvasCoords(e);
  const { x, y } = coords;

  // Check if clicking on a resize handle
  const handle = getHandleAtPoint(x, y);
  if (handle) {
    selectorState.isResizing = true;
    selectorState.resizeHandle = handle;
    selectorState.dragStart = {
      x, y,
      selX: selectorState.selection.canvasX,
      selY: selectorState.selection.canvasY,
      selW: selectorState.selection.canvasWidth,
      selH: selectorState.selection.canvasHeight
    };
    return;
  }

  // Check if clicking inside existing selection (drag to move)
  if (isPointInSelection(x, y)) {
    selectorState.isDragging = true;
    selectorState.dragStart = {
      x, y,
      selX: selectorState.selection.canvasX,
      selY: selectorState.selection.canvasY
    };
    return;
  }

  // Start new selection
  selectorState.isSelecting = true;
  selectorState.selectionStart = coords;
  selectorState.selection = null;

  // Clear previous selection visual
  redrawCanvas();
}

/**
 * Handle mouse move - update selection, drag, or resize
 */
function handleSelectionMove(e) {
  const coords = getCanvasCoords(e);
  const { x, y } = coords;

  // Update cursor when not in any mode
  if (!selectorState.isSelecting && !selectorState.isDragging && !selectorState.isResizing) {
    updateCursor(x, y);
  }

  // Handle resize
  if (selectorState.isResizing && selectorState.dragStart) {
    handleResize(x, y);
    return;
  }

  // Handle drag (move)
  if (selectorState.isDragging && selectorState.dragStart) {
    handleDrag(x, y);
    return;
  }

  // Handle new selection drawing
  if (selectorState.isSelecting && selectorState.selectionStart) {
    const start = selectorState.selectionStart;
    const selX = Math.min(start.x, x);
    const selY = Math.min(start.y, y);
    const selW = Math.abs(x - start.x);
    const selH = Math.abs(y - start.y);
    drawSelection(selX, selY, selW, selH);
  }
}

/**
 * Handle resize operation
 */
function handleResize(x, y) {
  const { dragStart, resizeHandle, canvas } = selectorState;
  const { selX, selY, selW, selH } = dragStart;

  let newX = selX, newY = selY, newW = selW, newH = selH;

  // Apply resize based on handle
  switch (resizeHandle) {
    case 'nw':
      newX = Math.min(x, selX + selW - 20);
      newY = Math.min(y, selY + selH - 20);
      newW = selX + selW - newX;
      newH = selY + selH - newY;
      break;
    case 'ne':
      newY = Math.min(y, selY + selH - 20);
      newW = Math.max(20, x - selX);
      newH = selY + selH - newY;
      break;
    case 'sw':
      newX = Math.min(x, selX + selW - 20);
      newW = selX + selW - newX;
      newH = Math.max(20, y - selY);
      break;
    case 'se':
      newW = Math.max(20, x - selX);
      newH = Math.max(20, y - selY);
      break;
    case 'n':
      newY = Math.min(y, selY + selH - 20);
      newH = selY + selH - newY;
      break;
    case 's':
      newH = Math.max(20, y - selY);
      break;
    case 'w':
      newX = Math.min(x, selX + selW - 20);
      newW = selX + selW - newX;
      break;
    case 'e':
      newW = Math.max(20, x - selX);
      break;
  }

  // Clamp to canvas bounds
  newX = Math.max(0, newX);
  newY = Math.max(0, newY);
  newW = Math.min(newW, canvas.width - newX);
  newH = Math.min(newH, canvas.height - newY);

  drawSelection(newX, newY, newW, newH);
  updateTempSelection(newX, newY, newW, newH);
}

/**
 * Handle drag (move) operation
 */
function handleDrag(x, y) {
  const { dragStart, canvas, selection } = selectorState;
  const dx = x - dragStart.x;
  const dy = y - dragStart.y;

  let newX = dragStart.selX + dx;
  let newY = dragStart.selY + dy;

  // Clamp to canvas bounds
  newX = Math.max(0, Math.min(newX, canvas.width - selection.canvasWidth));
  newY = Math.max(0, Math.min(newY, canvas.height - selection.canvasHeight));

  drawSelection(newX, newY, selection.canvasWidth, selection.canvasHeight);
  updateTempSelection(newX, newY, selection.canvasWidth, selection.canvasHeight);
}

/**
 * Update temporary selection during drag/resize (without finalizing)
 */
function updateTempSelection(canvasX, canvasY, canvasWidth, canvasHeight) {
  const { scale } = selectorState;
  selectorState.selection = {
    x: Math.round(canvasX * scale.x),
    y: Math.round(canvasY * scale.y),
    width: Math.round(canvasWidth * scale.x),
    height: Math.round(canvasHeight * scale.y),
    canvasX, canvasY, canvasWidth, canvasHeight
  };
  updateSelectionInfo();
}

/**
 * Handle mouse up - finalize selection, drag, or resize
 */
function handleSelectionEnd(e) {
  const coords = getCanvasCoords(e);

  // End resize
  if (selectorState.isResizing) {
    selectorState.isResizing = false;
    selectorState.resizeHandle = null;
    selectorState.dragStart = null;
    finalizeSelection();
    return;
  }

  // End drag
  if (selectorState.isDragging) {
    selectorState.isDragging = false;
    selectorState.dragStart = null;
    finalizeSelection();
    return;
  }

  // End new selection
  if (selectorState.isSelecting && selectorState.selectionStart) {
    selectorState.isSelecting = false;
    const start = selectorState.selectionStart;
    const { x, y } = coords;

    const selX = Math.min(start.x, x);
    const selY = Math.min(start.y, y);
    const selW = Math.abs(x - start.x);
    const selH = Math.abs(y - start.y);

    // Minimum selection size
    if (selW > 10 && selH > 10) {
      updateTempSelection(selX, selY, selW, selH);
      finalizeSelection();
    } else {
      // Selection too small, clear it
      selectorState.selection = null;
      const extractBtn = document.getElementById('btnExtractSelection');
      if (extractBtn) extractBtn.disabled = true;
      redrawCanvas();
      hideSelectionInfo();
    }

    selectorState.selectionStart = null;
  }
}

/**
 * Finalize selection and enable extract button
 */
function finalizeSelection() {
  if (!selectorState.selection) return;

  const { selection } = selectorState;

  // Enable extract button
  const extractBtn = document.getElementById('btnExtractSelection');
  if (extractBtn) extractBtn.disabled = false;

  // Redraw with final selection
  drawSelection(
    selection.canvasX,
    selection.canvasY,
    selection.canvasWidth,
    selection.canvasHeight
  );

  console.log('[PanelSelector] Selection:', selection.width, 'x', selection.height);
}

/**
 * Select the entire image
 */
function selectAll() {
  const { canvas, image, scale } = selectorState;
  if (!canvas || !image) return;

  // Select full canvas area
  const canvasX = 0;
  const canvasY = 0;
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Create selection covering full image
  selectorState.selection = {
    x: 0,
    y: 0,
    width: image.naturalWidth,
    height: image.naturalHeight,
    canvasX,
    canvasY,
    canvasWidth,
    canvasHeight
  };

  // Draw and enable extract
  drawSelection(canvasX, canvasY, canvasWidth, canvasHeight);
  updateSelectionInfo();

  const extractBtn = document.getElementById('btnExtractSelection');
  if (extractBtn) extractBtn.disabled = false;

  console.log('[PanelSelector] Selected all:', image.naturalWidth, 'x', image.naturalHeight);
}

// ============================================================================
// TOUCH SUPPORT
// ============================================================================

function handleTouchStart(e) {
  e.preventDefault();
  const touch = e.touches[0];
  handleSelectionStart({ clientX: touch.clientX, clientY: touch.clientY });
}

function handleTouchMove(e) {
  e.preventDefault();
  const touch = e.touches[0];
  handleSelectionMove({ clientX: touch.clientX, clientY: touch.clientY });
}

function handleTouchEnd(e) {
  const touch = e.changedTouches[0];
  handleSelectionEnd({ clientX: touch.clientX, clientY: touch.clientY });
}

// ============================================================================
// CANVAS DRAWING (overlay only - no image drawing to avoid CORS)
// ============================================================================

/**
 * Clear the overlay canvas (makes it transparent, showing image underneath)
 */
function redrawCanvas() {
  const { ctx, canvas } = selectorState;
  if (!ctx || !canvas) return;

  // Just clear the overlay - image is displayed via img element underneath
  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

/**
 * Draw selection rectangle with dimmed overlay (no image drawing - CORS safe)
 */
function drawSelection(x, y, width, height) {
  const { ctx, canvas } = selectorState;
  if (!ctx || !canvas) return;

  // Clear overlay first
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw semi-transparent overlay over entire canvas
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Cut out the selection area (show image through)
  ctx.clearRect(x, y, width, height);

  // Selection border (green solid for better visibility)
  ctx.strokeStyle = '#0f0';
  ctx.lineWidth = 2;
  ctx.setLineDash([]);
  ctx.strokeRect(x, y, width, height);

  // Draw resize handles - corners and edge midpoints
  const hs = HANDLE_SIZE;
  ctx.fillStyle = '#0f0';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 1;

  // Corner handles
  const corners = [
    [x, y],                      // NW
    [x + width, y],              // NE
    [x, y + height],             // SW
    [x + width, y + height]      // SE
  ];

  // Edge midpoint handles
  const edges = [
    [x + width/2, y],            // N
    [x + width/2, y + height],   // S
    [x, y + height/2],           // W
    [x + width, y + height/2]    // E
  ];

  // Draw all handles
  [...corners, ...edges].forEach(([cx, cy]) => {
    ctx.fillStyle = '#0f0';
    ctx.fillRect(cx - hs/2, cy - hs/2, hs, hs);
    ctx.strokeStyle = '#000';
    ctx.strokeRect(cx - hs/2, cy - hs/2, hs, hs);
  });

  // Dimensions text with background for readability
  if (selectorState.image) {
    const realW = Math.round(width * selectorState.scale.x);
    const realH = Math.round(height * selectorState.scale.y);
    const text = `${realW} x ${realH}`;
    ctx.font = '12px "Space Mono", monospace';
    const textWidth = ctx.measureText(text).width;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(x + 4, y + height - 22, textWidth + 8, 18);

    // Text
    ctx.fillStyle = '#0f0';
    ctx.fillText(text, x + 8, y + height - 8);
  }
}

/**
 * Update selection info display
 */
function updateSelectionInfo() {
  const infoEl = document.getElementById('panelSelectorInfo');
  if (!infoEl || !selectorState.selection) return;

  const { width, height } = selectorState.selection;
  infoEl.querySelector('.selection-dimensions').textContent = `${width} x ${height} px`;
  infoEl.classList.add('visible');
}

/**
 * Hide selection info
 */
function hideSelectionInfo() {
  const infoEl = document.getElementById('panelSelectorInfo');
  if (infoEl) infoEl.classList.remove('visible');
}

// ============================================================================
// EXTRACTION
// ============================================================================

/**
 * Extract selection as new panel via API
 */
async function extractSelection() {
  const { sourcePanel, selection } = selectorState;

  if (!selection || selection.width < 10 || selection.height < 10) {
    console.warn('[PanelSelector] No valid selection to extract');
    return;
  }

  const extractBtn = document.getElementById('btnExtractSelection');
  if (extractBtn) {
    extractBtn.disabled = true;
    extractBtn.textContent = 'EXTRACTING...';
  }

  try {
    // Build API request
    const baseUrl = window.API_CONFIG?.baseUrl || '';
    const response = await fetch(`${baseUrl}/api/storyboard/split`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        imageUrl: sourcePanel.url,
        rows: 1,
        cols: 1,
        selection: {
          x: selection.x,
          y: selection.y,
          width: selection.width,
          height: selection.height
        }
      })
    });

    const result = await response.json();

    if (result.success && result.data?.panels?.length > 0) {
      const newPanel = result.data.panels[0];
      const ideaId = sourcePanel.ideaId;

      // Add to panel pool (accumulate mode - don't replace)
      addPanelsToPool(ideaId, [{
        dataUrl: newPanel.url || newPanel.dataUrl,
        url: newPanel.url || newPanel.dataUrl,
        path: newPanel.path,
        source: 'selection',
        sourceIndex: sourcePanel.index,
        width: newPanel.width,
        height: newPanel.height,
        extractedAt: new Date().toISOString()
      }], false);

      // Save to database if it's a saved idea
      if (isDatabaseIdea(ideaId)) {
        await saveToDatabase(ideaId);
        console.log('[PanelSelector] Panel saved to database');
      }

      // Close modal
      closePanelSelector();

      // Re-render panel library (use window global to avoid circular import)
      if (window.renderPanelsView) {
        window.renderPanelsView();
      }

      // Show success toast
      if (window.showToast) {
        window.showToast('Selection extracted as new panel', 'success');
      } else if (window.Toast?.success) {
        window.Toast.success('Selection extracted as new panel');
      }

      console.log('[PanelSelector] Extracted selection as new panel:', newPanel.width, 'x', newPanel.height);
    } else {
      throw new Error(result.error || 'Failed to extract selection');
    }
  } catch (error) {
    console.error('[PanelSelector] Extraction failed:', error);

    if (window.showToast) {
      window.showToast('Failed to extract selection: ' + error.message, 'error');
    } else if (window.Toast?.error) {
      window.Toast.error('Failed to extract selection');
    } else {
      alert('Failed to extract selection: ' + error.message);
    }

    if (extractBtn) {
      extractBtn.disabled = false;
      extractBtn.textContent = 'EXTRACT SELECTION';
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export { selectorState };
